import {copyText} from '@github-ui/copy-to-clipboard'
import {useCurrentRepository} from '@github-ui/current-repository'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {
  AgentIcon,
  CodeIcon,
  CodespacesIcon,
  CopyIcon,
  DesktopDownloadIcon,
  TerminalIcon,
  TriangleDownIcon,
  VscodeIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, ButtonGroup} from '@primer/react'
import type {Pull} from '../../types/pull'
import {buildGitHubDesktopBranchUrl} from '../../utils/github-desktop'
import styles from './OpenInMenuV2.module.css'

function attemptOpenUrlWithFallback(url: string, repositoryNwo: string) {
  const command = `git clone https://github.com/${repositoryNwo}.git && code ${repositoryNwo.split('/')[1]}`

  window.open(url, '_self')

  // Fallback: let's also copy to clipboard a basic git clone command
  if (navigator.clipboard) {
    void navigator.clipboard.writeText(command)
  }
}

function buildVSCodeAgentSessionUrl(
  protocol: 'vscode' | 'vscode-insiders',
  repositoryNwo: string,
  globalId: string,
  headRefName: string,
): string {
  const repoUrl = `https://github.com/${repositoryNwo}.git`
  return `${protocol}://GitHub.copilot-chat/openAgentSession?id=${globalId}&type=copilot&url=${encodeURIComponent(
    repoUrl,
  )}&branch=${encodeURIComponent(headRefName)}`
}

const createHandleOpenInVSCode = (repositoryNwo: string, globalId: string, headRefName: string) => () => {
  const vscodeUrl = buildVSCodeAgentSessionUrl('vscode', repositoryNwo, globalId, headRefName)
  attemptOpenUrlWithFallback(vscodeUrl, repositoryNwo)
}

const createHandleOpenInVSCodeInsiders = (repositoryNwo: string, globalId: string, headRefName: string) => () => {
  const vscodeInsidersUrl = buildVSCodeAgentSessionUrl('vscode-insiders', repositoryNwo, globalId, headRefName)
  attemptOpenUrlWithFallback(vscodeInsidersUrl, repositoryNwo)
}

interface OpenInMenuV2Props {
  branchName: string
  pull?: Pull
  taskId?: string
  isCurrentUserTaskCreator?: boolean
}

export function OpenInMenuV2({pull, branchName, taskId, isCurrentUserTaskCreator}: OpenInMenuV2Props) {
  const repo = useCurrentRepository()

  // Centralized CLI resume logic shared by both the primary button and the dropdown fallback
  const disableCLIResume = isFeatureEnabled('copilot_mc_cli_resume_any_users_task') && !isCurrentUserTaskCreator
  // Prefer the Mission Control task ID: the Copilot CLI resolves it via
  // GET /agents/tasks/{taskId}. PR GraphQL relay IDs (PR_*) are not
  // recognized by the CLI and cause "No session or task matched".
  const resumeTarget = taskId ?? pull?.global_relay_id
  const cliCommand = resumeTarget ? `copilot --resume=${resumeTarget}` : undefined

  // Button on left side of button group (default action when PR is present)
  const getContinueInCopilotCLIButton = () => {
    if (!pull || !cliCommand) {
      return null
    }

    return (
      <Button
        onClick={() => {
          sendEvent('repo_agents', {
            target: 'continue_in_copilot_cli',
            task_id: taskId,
            pull_id: pull.global_relay_id,
          })
          copyText(cliCommand)
        }}
        leadingVisual={<AgentIcon />}
        trailingVisual={<CopyIcon />}
        disabled={disableCLIResume}
      >
        Continue in Copilot CLI
      </Button>
    )
  }

  // Menu button on right side of button group (or standalone button if no PR)
  const getActionMenuButton = () => {
    if (pull) {
      return <ActionMenu.Button aria-label="More options" icon={<TriangleDownIcon className={styles.dropdownIcon} />} />
    }

    return <ActionMenu.Button leadingVisual={<CodeIcon />}>Code</ActionMenu.Button>
  }

  const getOpenInVSCodeItem = () => {
    // TODO: Allow this for non-PR tasks once VS Code supports task ID
    if (!pull) {
      return null
    }

    const handleOpenInVSCode = createHandleOpenInVSCode(pull.repository_nwo, pull.global_relay_id, pull.head_ref_name)

    return (
      <ActionList.Item
        onSelect={() => {
          sendEvent('repo_agents', {
            target: 'open_in_vscode',
            task_id: taskId,
            pull_id: pull.global_relay_id,
            branch_name: branchName,
          })
          handleOpenInVSCode()
        }}
      >
        <ActionList.LeadingVisual>
          <VscodeIcon size={16} />
        </ActionList.LeadingVisual>
        Open in VS Code
      </ActionList.Item>
    )
  }

  const getOpenInVSCodeInsidersItem = () => {
    // TODO: Allow this for non-PR tasks once VS Code supports task ID
    if (!pull) {
      return null
    }

    const handleOpenInVSCodeInsiders = createHandleOpenInVSCodeInsiders(
      pull.repository_nwo,
      pull.global_relay_id,
      pull.head_ref_name,
    )

    return (
      <ActionList.Item
        onSelect={() => {
          sendEvent('repo_agents', {
            target: 'open_in_vscode_insiders',
            task_id: taskId,
            pull_id: pull.global_relay_id,
            branch_name: branchName,
          })
          handleOpenInVSCodeInsiders()
        }}
      >
        <ActionList.LeadingVisual>
          <VscodeIcon size={16} />
        </ActionList.LeadingVisual>
        Open in VS Code Insiders
      </ActionList.Item>
    )
  }

  const getContinueInCopilotCLIItem = () => {
    if (!cliCommand) {
      return null
    }

    return (
      <>
        <ActionList.Item
          onSelect={() => {
            sendEvent('repo_agents', {
              target: 'continue_in_copilot_cli',
              task_id: taskId,
              pull_id: pull?.global_relay_id,
            })
            copyText(cliCommand)
          }}
          disabled={disableCLIResume}
          inactiveText={disableCLIResume ? 'Only session creator can resume' : undefined}
        >
          <ActionList.LeadingVisual>
            <AgentIcon />
          </ActionList.LeadingVisual>
          Continue in Copilot CLI
          {!disableCLIResume ? (
            <ActionList.Description className={styles.cliDescription} variant="block">
              {cliCommand}
            </ActionList.Description>
          ) : null}
          <ActionList.TrailingVisual>
            <CopyIcon size={16} />
          </ActionList.TrailingVisual>
        </ActionList.Item>
        <ActionList.Divider />
      </>
    )
  }

  const getCheckoutWithGitHubCLIItem = () => {
    return (
      <ActionList.Item
        onSelect={() => {
          sendEvent('repo_agents', {
            target: 'checkout_with_github_cli',
            task_id: taskId,
            pull_id: pull?.global_relay_id,
          })
          copyText(`gh pr checkout ${branchName}`)
        }}
      >
        <ActionList.LeadingVisual>
          <TerminalIcon size={16} />
        </ActionList.LeadingVisual>
        Checkout with GitHub CLI
        <ActionList.TrailingVisual>
          <CopyIcon size={16} />
        </ActionList.TrailingVisual>
      </ActionList.Item>
    )
  }

  const getCreateCodespaceItem = () => {
    if (!repo) {
      return null
    }

    const createCodespacePath = `/codespaces/new?skip_quickstart=true&repo=${repo.id}&ref=${encodeURIComponent(
      branchName,
    )}`
    return (
      <ActionList.Item
        onSelect={() => {
          sendEvent('repo_agents', {
            target: 'create_codespace',
            task_id: taskId,
            pull_id: pull?.global_relay_id,
            repo_id: repo.id.toString(),
          })
          window.open(createCodespacePath, '_self')
        }}
      >
        <ActionList.LeadingVisual>
          <CodespacesIcon size={16} />
        </ActionList.LeadingVisual>
        Create a codespace
      </ActionList.Item>
    )
  }

  const getCheckoutWithGitHubDesktopItem = () => {
    if (!repo) {
      return null
    }

    const repoNwo = `${repo.ownerLogin}/${repo.name}`
    const desktopUrl = buildGitHubDesktopBranchUrl(repoNwo, branchName)
    if (!desktopUrl) {
      return null
    }

    return (
      <ActionList.Item
        onSelect={() => {
          sendEvent('repo_agents', {
            target: 'checkout_with_github_desktop',
            task_id: taskId,
            pull_id: pull?.global_relay_id,
            repo_nwo: repoNwo,
          })
          attemptOpenUrlWithFallback(desktopUrl, repoNwo)
        }}
      >
        <ActionList.LeadingVisual>
          <DesktopDownloadIcon size={16} />
        </ActionList.LeadingVisual>
        Checkout with GitHub Desktop
      </ActionList.Item>
    )
  }

  const getActionMenu = () => {
    return (
      <ActionMenu>
        {getActionMenuButton()}
        <ActionMenu.Overlay className={styles.overlay} align="end" side="outside-bottom">
          <ActionList>
            {pull ? (
              <>
                {disableCLIResume && getContinueInCopilotCLIItem()}
                {getOpenInVSCodeItem()}
                {getOpenInVSCodeInsidersItem()}
                <ActionList.Divider />
              </>
            ) : (
              getContinueInCopilotCLIItem()
            )}
            {getCheckoutWithGitHubCLIItem()}
            {getCreateCodespaceItem()}
            {getCheckoutWithGitHubDesktopItem()}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    )
  }

  return (
    <div>
      {pull ? (
        <ButtonGroup className="hide-sm">
          {getContinueInCopilotCLIButton()}
          {getActionMenu()}
        </ButtonGroup>
      ) : (
        getActionMenu()
      )}
    </div>
  )
}
